import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useAnimation } from 'framer-motion';
import useTheme from '../ColorConfig/usetheme';

// Import testimonial avatars - you'll need to add these images to your assets folder
import avatar1 from '../../Vaanmathi/assets/avt1.png';
import avatar2 from '../../Vaanmathi/assets/avt2.png';
import avatar3 from '../../Vaanmathi/assets/avt3.png';
import avatar4 from '../../Vaanmathi/assets/avt4.png';
import avatar5 from '../../Vaanmathi/assets/avt5.png';

const TestimonialsSection = () => {
  const colors = useTheme();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef(null);
  const scrollTrackRef = useRef(null);
  
  // Drag scroll variables
  const isDragging = useRef(false);
  const startX = useRef(0);
  const currentX = useRef(0);
  const scrollVelocity = useRef(0);
  const lastX = useRef(0);
  const timestamp = useRef(0);
  const animationFrame = useRef(null);
  
  const controls = useAnimation();

  // Hardcoded testimonials data
  const hardcodedTestimonials = [
    {
      id: "1",
      quote: "TechFlow transformed our complex workflow into a streamlined, efficient system. Their AI solution is robust and user-friendly.",
      name: "Sarah Chen",
      position: "CTO",
      company: "InnovateTech Solutions",
      avatar: avatar1
    },
    {
      id: "2",
      quote: "The automation platform developed by TechFlow has revolutionized our customer service with significant improvements.",
      name: "Michael Rodriguez",
      position: "Operations Director",
      company: "Global Retail Corp",
      avatar: avatar2
    },
    {
      id: "3",
      quote: "Our new analytics dashboard is not just visually appealing but incredibly performant. They exceeded expectations.",
      name: "Emily Watson",
      position: "Head of Analytics",
      company: "DataDrive Insights",
      avatar: avatar3
    },
    {
      id: "4",
      quote: "TechFlow's cloud migration service was seamless and efficient. We reduced costs by 40% while scaling operations.",
      name: "David Park",
      position: "Infrastructure Lead",
      company: "CloudFirst Technologies",
      avatar: avatar4
    },
    {
      id: "5",
      quote: "The mobile application transformed how our field teams operate with real-time sync and intuitive design.",
      name: "Lisa Thompson",
      position: "Field Operations Manager",
      company: "MobileTech Solutions",
      avatar: avatar5
    }
  ];

  // Commented out original API fetching function for future use
  /*
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch('https://api.dremerz.net/api/DZ_8923_Idlbay_Testimonials_TestimonialsList');
        const data = await response.json();
        setTestimonials(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);
  */

  // Load hardcoded data
  useEffect(() => {
    const loadHardcodedData = () => {
      try {
        setLoading(true);
        setTestimonials(hardcodedTestimonials);
      } catch (error) {
        console.error('Error loading hardcoded testimonials:', error);
      } finally {
        setLoading(false);
      }
    };

    loadHardcodedData();
  }, []);

  // Improved continuous auto-scroll animation
  useEffect(() => {
    if (testimonials.length === 0 || isPaused) return;

    const startAutoScroll = async () => {
      const cardWidth = 374;
      const gap = 24;
      const singleSetWidth = (cardWidth + gap) * testimonials.length;
      
      // Start from current position for seamless transition
      const currentPosition = currentX.current;
      
      await controls.start({
        x: [currentPosition, currentPosition - singleSetWidth],
        transition: {
          duration: testimonials.length * 2, // Adjust speed as needed
          ease: "linear",
          repeat: Infinity,
          repeatType: "loop"
        }
      });
    };

    startAutoScroll();
  }, [testimonials.length, isPaused, controls]);

  // Get current X position safely
  const getCurrentX = useCallback(() => {
    try {
      if (scrollTrackRef.current) {
        const transform = window.getComputedStyle(scrollTrackRef.current).transform;
        if (transform && transform !== 'none') {
          const matrix = new DOMMatrix(transform);
          return matrix.m41;
        }
      }
      return currentX.current;
    } catch (error) {
      return currentX.current;
    }
  }, []);

  // Handle momentum scroll after release
  const handleMomentumScroll = useCallback(() => {
    if (!scrollTrackRef.current || Math.abs(scrollVelocity.current) < 0.1) {
      scrollVelocity.current = 0;
      return;
    }

    const cardWidth = 374;
    const gap = 24;
    const maxScroll = -((cardWidth + gap) * testimonials.length);
    
    currentX.current += scrollVelocity.current;
    
    // Apply bounds with elastic effect
    if (currentX.current > 0) {
      currentX.current = 0;
      scrollVelocity.current = 0;
    } else if (currentX.current < maxScroll) {
      currentX.current = maxScroll;
      scrollVelocity.current = 0;
    }
    
    controls.set({ x: currentX.current });
    
    // Apply friction
    scrollVelocity.current *= 0.95;
    
    // Continue animation if still has velocity
    if (Math.abs(scrollVelocity.current) > 0.1) {
      animationFrame.current = requestAnimationFrame(handleMomentumScroll);
    } else {
      scrollVelocity.current = 0;
    }
  }, [controls, testimonials.length]);

  // Handle mouse enter - pause animation
  const handleMouseEnter = useCallback(() => {
    setIsPaused(true);
    controls.stop();
  }, [controls]);

  // Handle mouse leave - resume animation
  const handleMouseLeave = useCallback(() => {
    if (!isDragging.current) {
      setIsPaused(false);
    }
  }, []);

  // Handle mouse down event for dragging
  const handleMouseDown = useCallback((e) => {
    if (!scrollTrackRef.current) return;
    
    setIsPaused(true);
    isDragging.current = true;
    controls.stop();
    
    startX.current = e.pageX;
    currentX.current = getCurrentX();
    lastX.current = e.pageX;
    timestamp.current = Date.now();
    scrollVelocity.current = 0;
    
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }
    
    scrollTrackRef.current.style.cursor = 'grabbing';
    scrollTrackRef.current.style.userSelect = 'none';
    
    e.preventDefault();
  }, [controls, getCurrentX]);

  // Handle mouse move event for dragging
  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current || !scrollTrackRef.current) return;
    
    const now = Date.now();
    const deltaTime = now - timestamp.current;
    
    if (deltaTime > 0) {
      const x = e.pageX;
      const deltaX = x - lastX.current;
      
      // Calculate velocity for smooth scrolling
      scrollVelocity.current = deltaX * (16 / Math.max(deltaTime, 1));
      
      const walk = (x - startX.current);
      const newX = currentX.current + walk;
      
      // Apply bounds with soft limits
      const cardWidth = 374;
      const gap = 24;
      const maxScroll = -((cardWidth + gap) * testimonials.length);
      const boundedX = Math.min(Math.max(newX, maxScroll - 100), 100);
      
      controls.set({ x: boundedX });
      
      lastX.current = x;
      timestamp.current = now;
    }
  }, [controls, testimonials.length]);

  // Handle mouse up event for dragging
  const handleMouseUp = useCallback(() => {
    if (!isDragging.current) return;
    
    isDragging.current = false;
    currentX.current = getCurrentX();
    
    if (scrollTrackRef.current) {
      scrollTrackRef.current.style.cursor = 'grab';
      scrollTrackRef.current.style.userSelect = '';
    }
    
    // Start momentum scrolling
    if (Math.abs(scrollVelocity.current) > 0.5) {
      animationFrame.current = requestAnimationFrame(handleMomentumScroll);
    }
    
    setTimeout(() => {
      if (!isDragging.current) {
        setIsPaused(false);
      }
    }, 1000);
  }, [getCurrentX, handleMomentumScroll]);

  // Enhanced touch events for mobile - FIXED VERSION
  const handleTouchStart = useCallback((e) => {
    if (!scrollTrackRef.current) return;
    
    const touch = e.touches[0];
    const rect = scrollTrackRef.current.getBoundingClientRect();
    
    // Only handle touches that start within the testimonial track
    if (touch.clientX >= rect.left && touch.clientX <= rect.right &&
        touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
      
      setIsPaused(true);
      isDragging.current = true;
      controls.stop();
      
      startX.current = touch.pageX;
      currentX.current = getCurrentX();
      lastX.current = touch.pageX;
      timestamp.current = Date.now();
      scrollVelocity.current = 0;
      
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
      
      // Don't prevent default to allow page scrolling when needed
    }
  }, [controls, getCurrentX]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging.current || !scrollTrackRef.current) return;
    
    const now = Date.now();
    const deltaTime = now - timestamp.current;
    const touch = e.touches[0];
    
    if (deltaTime > 0) {
      const x = touch.pageX;
      const deltaX = x - lastX.current;
      
      // Check if this is primarily horizontal movement
      const touchMoveX = Math.abs(deltaX);
      const touchMoveY = Math.abs(touch.clientY - startX.current);
      
      // If movement is more horizontal than vertical, handle the scroll
      if (touchMoveX > touchMoveY * 1.5) {
        // Reduced sensitivity for mobile
        scrollVelocity.current = deltaX * (12 / Math.max(deltaTime, 1));
        
        const walk = (x - startX.current) * 1.2;
        const newX = currentX.current + walk;
        
        const cardWidth = 374;
        const gap = 24;
        const maxScroll = -((cardWidth + gap) * testimonials.length);
        const boundedX = Math.min(Math.max(newX, maxScroll - 100), 100);
        
        controls.set({ x: boundedX });
        
        lastX.current = x;
        timestamp.current = now;
        
        // Only prevent default for horizontal swipes to allow vertical page scrolling
        e.preventDefault();
      } else {
        // If movement is more vertical, release the drag to allow page scrolling
        isDragging.current = false;
        setIsPaused(false);
      }
    }
  }, [controls, testimonials.length]);

  const handleTouchEnd = useCallback((e) => {
    if (!isDragging.current) return;
    
    isDragging.current = false;
    currentX.current = getCurrentX();
    
    // Start momentum scrolling with reduced velocity for mobile
    if (Math.abs(scrollVelocity.current) > 0.3) {
      scrollVelocity.current *= 0.8;
      animationFrame.current = requestAnimationFrame(handleMomentumScroll);
    }
    
    setTimeout(() => {
      if (!isDragging.current) {
        setIsPaused(false);
      }
    }, 800);
  }, [getCurrentX, handleMomentumScroll]);

  // Improved event listeners with better touch handling
  useEffect(() => {
    const trackElement = scrollTrackRef.current;
    
    if (trackElement) {
      trackElement.addEventListener('mousedown', handleMouseDown);
      trackElement.addEventListener('mouseenter', handleMouseEnter);
      trackElement.addEventListener('mouseleave', handleMouseLeave);
      
      // Use passive: true for touchstart to improve scrolling performance
      trackElement.addEventListener('touchstart', handleTouchStart, { passive: true });
      trackElement.addEventListener('touchmove', handleTouchMove, { passive: false });
      trackElement.addEventListener('touchend', handleTouchEnd, { passive: true });
    }
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      if (trackElement) {
        trackElement.removeEventListener('mousedown', handleMouseDown);
        trackElement.removeEventListener('mouseenter', handleMouseEnter);
        trackElement.removeEventListener('mouseleave', handleMouseLeave);
        trackElement.removeEventListener('touchstart', handleTouchStart);
        trackElement.removeEventListener('touchmove', handleTouchMove);
        trackElement.removeEventListener('touchend', handleTouchEnd);
      }
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, [handleMouseDown, handleMouseMove, handleMouseUp, handleMouseEnter, handleMouseLeave, 
      handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Animation variants - removed bottom animation
  const titleVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const subtitleVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut", delay: 0.2 }
    }
  };

  // Simplified card variants - removed bottom animation
  const cardVariants = {
    hidden: { 
      opacity: 0
    },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.6, 
        ease: "easeOut"
      }
    }
  };

  const renderTestimonialCard = (testimonial, index, keyPrefix = '') => (
    <motion.div
      key={`${keyPrefix}${testimonial.id}`}
      className="testimonial-card mb-2 mt-4"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      variants={cardVariants}
      whileHover={{ 
        scale: 1.05,
        rotateY: 5,
        z: 50,
        transition: { duration: 0.3, type: "spring", stiffness: 300 }
      }}
      style={{
        width: '350px',
        height: '200px',
        background: `linear-gradient(135deg, ${colors.cardBgBlack} 0%, ${colors.hoverBlack} 100%)`,
        border: `1px solid ${colors.gray}33`,
        borderRadius: '16px',
        overflow: 'hidden',
        position: 'relative',
        flexShrink: 0
      }}
    >
      {/* Animated border */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg, var(--color-primary) , var(--color-primary) , var(--color-primary) )`,
          opacity: 0,
          transition: 'opacity 0.3s ease'
        }}
        className="card-border"
      />
      
      {/* Glow effect */}
      <div
        style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: `radial-gradient(circle, var(--color-primary) 0%, transparent 70%)`,
          opacity: 0,
          transition: 'opacity 0.3s ease',
          pointerEvents: 'none'
        }}
        className="card-glow"
      />
      
      <div style={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 2 }}>
        <blockquote style={{ marginBottom: '16px', flex: 1 }}>
          <p style={{ 
            color: colors.white,
            fontSize: '0.9rem', 
            lineHeight: '1.5',
            margin: 0,
            fontStyle: 'italic',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            "{testimonial.quote}"
          </p>
        </blockquote>
        
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <motion.div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              overflow: 'hidden',
              marginRight: '12px',
              border: `2px solid var(--color-primary) 33`
            }}
            whileHover={{ 
              scale: 1.1,
              borderColor: `var(--color-primary)`,
              transition: { duration: 0.2 }
            }}
          >
            <img
              src={testimonial.avatar}
              alt={testimonial.name}
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover'
              }}
            />
          </motion.div>
          <div>
            <h6 style={{ 
              margin: 0,
              marginBottom: '2px',
              color: colors.white, 
              fontSize: '0.9rem',
              fontWeight: '600'
            }}>
              {testimonial.name}
            </h6>
            <p style={{ 
              margin: 0, 
              color: colors.white, 
              fontSize: '0.75rem',
              lineHeight: '1.3'
            }}>
              {testimonial.position}
              <br />
              <span style={{ color: `var(--color-primary)`, fontSize: '0.7rem' }}>
                {testimonial.company}
              </span>
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <section style={{ 
        backgroundColor: colors.bodyBlack, 
        padding: '100px 0',
        overflow: 'hidden',
        textAlign: 'center',
        color: colors.white
      }}>
        Loading testimonials...
      </section>
    );
  }

  return (
    <section style={{ 
      backgroundColor: colors.bodyBlack, 
      padding: '20px 0',
      overflow: 'hidden',
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 10px' }}>
        <div style={{ textAlign: 'center'}}>
          <motion.h2 
            className='client-title display-5 fw-bold '
            style={{ 
              fontWeight: '700',
              color: colors.white,
              marginBottom: '16px',
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={titleVariants}
          >
            Client Success Stories
          </motion.h2>
          
          <motion.h3 
            className='client-subtitle'
            style={{ 
              color: colors.white,
              
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={subtitleVariants}
          >
            Trusted by industry leaders worldwide
          </motion.h3>
        </div>
        
        <div 
          ref={containerRef}
          className="horizontal-scroll-container"
          style={{
            position: 'relative',
            overflow: 'hidden',
            mask: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
            WebkitMask: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)'
          }}
        >
          <motion.div 
            ref={scrollTrackRef}
            style={{
              display: 'flex',
              gap: '24px',
              width: 'fit-content',
              // cursor: 'pointer',
              padding: '10px 0'
            }}
            animate={controls}
            initial={{ x: 0 }}
          >
            {/* First set */}
            {testimonials.map((testimonial, index) => 
              renderTestimonialCard(testimonial, index, 'first-')
            )}
            
            {/* Second set for seamless loop */}
            {testimonials.map((testimonial, index) => 
              renderTestimonialCard(testimonial, index, 'second-')
            )}
          </motion.div>
        </div>
      </div>
      
      <style jsx>{`
        .client-title{
          font-size: 2.5rem;
        }
        .client-subtitle{
          font-size: 1.1rem;
        }
        .horizontal-scroll-container {
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: none;
          -ms-overflow-style: none;
          -webkit-overflow-scrolling: touch;
        }
        
        .horizontal-scroll-container::-webkit-scrollbar {
          display: none;
        }
        
        .testimonial-card:hover .card-border {
          opacity: 1 !important;
        }
        
        .testimonial-card:hover .card-glow {
          opacity: 0.3 !important;
        }
        
        .testimonial-card {
          flex-shrink: 0;
          transform-style: preserve-3d;
          transition: all 0.3s ease;
          -webkit-tap-highlight-color: transparent;
          touch-action: pan-y;
        }
        
        .testimonial-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, ${colors.primaryTransparent} 0%, transparent 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
          z-index: 1;
        }
        
        .testimonial-card:hover::before {
          opacity: 1;
        }
        
        .testimonial-card:hover {
          box-shadow: 0 20px 60px rgba(229, 118, 39, 0.2);
        }

        @media (max-width: 768px) {
          .client-title {
            font-size: 1.8rem;
          }
          .client-subtitle {
            font-size: 1rem;
          }
          
          .testimonial-card {
            width: 300px !important;
            height: 180px !important;
          }
        }

        @media (max-width: 480px) {
          .client-title {
            font-size: 1.5rem;
          }
          .client-subtitle {
            font-size: 0.9rem;
          }
          
          .testimonial-card {
            width: 280px !important;
            height: 170px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default TestimonialsSection;